import FontFaceObserver from "fontfaceobserver";
import { assembleCommon } from "google-fonts-css2/dist/prod";
import { useEffect, useRef, useState } from "react";

/**
 * Font family name and styles
 * @see https://fonts.google.com/
 */
export interface GoogleFontFamily {
  /**
   * Font family name. Eg: `Inter`, `Andika New Basic`
   */
  family: string;
  /**
   * Array of styles
   * @example
   * "600..700"
   * "100..200italic"
   * "300italic"
   * "regular"
   * "italic"
   * "500"
   * 444
   * @see https://developers.google.com/fonts/docs/css2
   * @see https://github.com/adriano-tirloni/google-fonts-css2
   */
  styles: (string | number)[];
}

export enum GoogleFontsStatus {
  LOADING = 0,
  LOADED = 1,
  FAILED = -1,
}

export type GoogleFontOptions = {
  /**
   * `"swap"` is recommended.
   */
  display: "swap" | "auto";
};

export const defaultOptions: GoogleFontOptions = {
  display: "swap",
};

/**
 * Returned object from the `useGoogleFonts` hook.
 */
export interface UseGoogleFontsResult {
  /**
   * Generated URL (Google Font css2)
   */
  hrefs: string[] | undefined;
  /**
   * Current operation status
   */
  status: GoogleFontsStatus;
  /**
   * Error object if `status` is `FAILED` (`-1`).
   */
  error: Error | undefined;
}

/**
 * Cross-module cache of Google Fonts hrefs we've already injected as
 * `<link rel="stylesheet">`. Stored on `window` so it survives HMR / code
 * splitting / multiple imports of this module — a plain `new Set()` at
 * module scope can get re-initialized in those scenarios, which manifested
 * as the same ~15 chunks of Google Fonts CSS being re-fetched after each
 * user action.
 */
const loadedHrefsCache: Set<string> = (() => {
  if (typeof window === "undefined") return new Set<string>();
  const w = window as any;
  if (!w.__googleFontsLoadedHrefsCache) {
    w.__googleFontsLoadedHrefsCache = new Set<string>();
  }
  return w.__googleFontsLoadedHrefsCache;
})();

/**
 * Load fonts from `fonts` argument by creating a single URL which will be appended to the `<head/>` of the document as `<link href={} rel="stylesheet" />`.
 *
 * Uses a module-level cache to prevent re-creating link tags for fonts that are already loaded.
 *
 * Get the operation status (and `error` if present) via the `status` attribute of the returned object.
 */
export function useGoogleFonts(
  fonts: GoogleFontFamily[] | undefined | null = [],
  options: GoogleFontOptions = defaultOptions
): UseGoogleFontsResult {
  const CHUNK = 100;

  const [status, setStatus] = useState(GoogleFontsStatus.LOADING);
  const [error, setError] = useState<Error>();
  const hasLoadedRef = useRef(false);
  const linksRef = useRef<HTMLLinkElement[]>([]);

  const temporary: GoogleFontFamily[][] = [];

  if (fonts) {
    for (let i = 0; i < fonts.length; i += CHUNK) {
      temporary.push(fonts.slice(i, i + CHUNK));
    }
  }

  const hrefs = temporary.map((f) => {
    return assembleCommon(f, options.display);
  });

  // Use fonts.length as a stable dependency instead of the array reference
  const fontsLength = fonts?.length ?? 0;

  useEffect(() => {
    if (!hrefs.length || hasLoadedRef.current) return;

    // Filter out hrefs that are already loaded. Two checks because the
    // in-memory `loadedHrefsCache` and the actual `<link>` tags in the DOM
    // can drift apart — e.g. the cache cleared but the tags survived, or
    // vice versa. We rely on whichever indicates "already there".
    const newHrefs = hrefs.filter((href) => {
      if (loadedHrefsCache.has(href)) return false;
      // CSS.escape isn't always available in older browsers; this href is
      // produced by `assembleCommon` and contains no quote characters, so
      // a direct attribute selector is safe.
      if (typeof document !== "undefined" && document.querySelector(`link[href="${href}"]`)) {
        // Tag exists in DOM but cache lost it — reconcile and skip.
        loadedHrefsCache.add(href);
        return false;
      }
      return true;
    });

    if (newHrefs.length === 0) {
      // All fonts already loaded
      setStatus(GoogleFontsStatus.LOADED);
      hasLoadedRef.current = true;
      return;
    }

    const links = newHrefs.map((href) => {
      const link = document.createElement("link");
      link.href = href;
      link.rel = "stylesheet";
      document.head.appendChild(link);
      loadedHrefsCache.add(href);
      return link;
    });

    linksRef.current = links;
    hasLoadedRef.current = true;

    // Only observe fonts that haven't been loaded yet
    const promises = fonts!
      .filter((font) => !document.fonts.check(`16px "${font.family}"`))
      .slice(0, 50) // Limit concurrent observers to prevent memory issues
      .map((font) =>
        new FontFaceObserver(font.family).load(null, 10000).catch(() => {
          // Silently ignore individual font load failures
        })
      );

    Promise.all(promises)
      .then(() => {
        setError(undefined);
        setStatus(GoogleFontsStatus.LOADED);
      })
      .catch((err) => {
        setError(err);
        setStatus(GoogleFontsStatus.FAILED);
      });

    // No cleanup — fonts stay loaded for the session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontsLength]);

  return { hrefs, status, error };
}
